import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { selectionProcessesAdmin } from '@/services/selectionProcesses';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PATHS } from '@/routes/paths';
import OverviewTab from './OverviewTab';
import RegistrationsTab from './RegistrationsTab';
import PublicLinkCard from './PublicLinkCard';
import { DashboardContent } from '@/layouts/dashboard';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', variant: 'warning' },
  open: { label: 'Aberto', variant: 'success' },
  closed: { label: 'Encerrado', variant: 'neutral-lightest' },
};

function PageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-64" />
        <div className="flex gap-2">
          <Skeleton className="h-10 w-20 rounded-full" />
          <Skeleton className="h-10 w-24 rounded-full" />
        </div>
      </div>
      <Skeleton className="h-24 w-full rounded-xl" />
      <Skeleton className="h-10 w-48 rounded-full" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}

export default function SelectionProcessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [process, setProcess] = useState(null);
  const load = () => selectionProcessesAdmin.get(id).then(res => setProcess(res.data));

  useEffect(() => {
    load();
  }, [id]);

  const breadcrumbs = {
    title: 'Inscrições',
    nav: [
      {
        route: PATHS.ADMIN_SELETIVO.LIST,
        name: 'Seletivos',
        isActive: false,
      },
      {
        name: process?.attributes?.name ?? '…',
        isActive: true,
      },
    ],
  };

  if (!process) {
    return (
      <DashboardContent variant="plain" breadcrumbs={breadcrumbs}>
        <PageSkeleton />
      </DashboardContent>
    );
  }

  const a = process.attributes;
  const statusCfg = STATUS_CONFIG[a.status] ?? STATUS_CONFIG.closed;

  return (
    <DashboardContent variant="plain" breadcrumbs={breadcrumbs}>
      {/* ── Page header ── */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="mb-0 text-xl font-bold leading-tight text-primary-dark">{a.name}</h1>
          <Badge variant={statusCfg.variant} size="sm">
            {statusCfg.label}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(PATHS.ADMIN_SELETIVO.EDIT(id))}>
            Editar
          </Button>
          {a.status === 'draft' && (
            <Button size="sm" onClick={() => selectionProcessesAdmin.publish(id).then(load)}>
              Publicar
            </Button>
          )}
          {a.status === 'open' && (
            <Button variant="outline-danger" size="sm" onClick={() => selectionProcessesAdmin.close(id).then(load)}>
              Encerrar
            </Button>
          )}
        </div>
      </div>

      {/* ── Public link ── */}
      <div className="mb-6">
        <PublicLinkCard url={`${window.location.origin}/inscricao/${a.slug}`} />
      </div>

      {/* ── Tabs ── */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Resumo</TabsTrigger>
          <TabsTrigger value="registrations">Inscritos</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <OverviewTab processId={id} status={a.status} />
        </TabsContent>
        <TabsContent value="registrations">
          <RegistrationsTab processId={id} slug={a.slug} />
        </TabsContent>
      </Tabs>
    </DashboardContent>
  );
}
